// Held-equipment rules shared by the Equipment screen and the equipment save route.
// 2024 rules: a Two-Handed weapon needs both hands, so nothing may sit in the off hand
// beside it (the app applies this while equipped, not only while attacking).
// Copies: an item can fill as many slots as the character owns copies of it.

const text = (value) => String(value ?? '').trim().toLowerCase();

const propertyNames = (item) => {
  const raw = item?.properties;
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(/[,;]/) : [];
  return list.map((entry) => text(entry?.name ?? entry)).filter(Boolean);
};

export const equipmentItemName = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (Array.isArray(item)) return String(item[0] ?? '');
  return item.displayName || item.name || item.itemName || item.accessoryName || item.armorName || item.title || '';
};

export const isTwoHandedWeapon = (item) => propertyNames(item).some((name) => /^two[\s-]?handed/.test(name));

export const isShield = (item) => [item?.category, item?.type, item?.armorType].some((value) => text(value).includes('shield'));

/** Returns a player-facing reason the main/off hand combination is not allowed, or null. */
export const getHandConflict = (equipment = {}) => {
  const main = equipment?.mainHand;
  const off = equipment?.offHand;
  if (off && isTwoHandedWeapon(off)) {
    return `${equipmentItemName(off) || 'That weapon'} is two-handed, so it goes in the main hand.`;
  }
  if (main && off && isTwoHandedWeapon(main)) {
    return `${equipmentItemName(main) || 'Your main-hand weapon'} needs both hands. Unequip ${equipmentItemName(off) || 'the off-hand item'} first.`;
  }
  if (main && isShield(main)) return 'A shield goes in the off hand.';
  return null;
};

const SOURCE_LISTS = {
  weapon: (character) => character?.weapon,
  armor: (character) => character?.armor,
  item: (character) => character?.item,
  accessory: (character) => character?.accessories ?? character?.accessory,
};

const ownedCopies = (list, name) => (Array.isArray(list) ? list : []).reduce((total, entry) => {
  if (!entry || (typeof entry === 'object' && !Array.isArray(entry) && entry.owned === false)) return total;
  if (text(equipmentItemName(entry)) !== name) return total;
  const quantity = Number(entry?.quantity ?? entry?.qty ?? entry?.count ?? 1);
  return total + (Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1);
}, 0);

/**
 * Returns a reason when the same item is equipped in more slots than the character owns copies,
 * or null. Only items equipped more than once are checked, so single equips keep working for
 * gear that is not tracked in the inventory lists.
 */
export const getCopyConflict = (equipment = {}, character = {}) => {
  const groups = new Map();
  Object.values(equipment || {}).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const source = text(item.source ?? item.__source);
    const name = text(equipmentItemName(item));
    if (!source || !name || !SOURCE_LISTS[source]) return;
    const key = `${source}::${name}`;
    groups.set(key, { source, name, label: equipmentItemName(item), count: (groups.get(key)?.count || 0) + 1 });
  });
  for (const { source, name, label, count } of groups.values()) {
    if (count < 2) continue;
    const owned = ownedCopies(SOURCE_LISTS[source](character), name);
    if (count > owned) {
      return `You own ${owned === 0 ? 'no' : owned} ${label}${owned === 1 ? '' : ' copies'}, so it can't fill ${count} slots.`;
    }
  }
  return null;
};
